import './../../Player.css';
import React, {useContext, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerContext } from '../../PlayerContext';
import { openFullscreen, browserLocales, unescapeHtml } from '../../Utils';
import { analyticsManager } from '../../../index';
import SecondaryButton, {nearType} from '../components/SecondaryButton';
import FadeInPanel from '../components/FadeInPanel';
import { useAnimate } from 'framer-motion';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getStoredPixelConsent } from '../../PixelConsentOverlay';
import { hasChainRestaurants } from '../../chainRestaurantUtils';

function NearButton(props){
    const { t } = useTranslation();
    const [isOpened, setIsOpened] = useState(false);

    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const propagateFocusChange = useContext(PlayerContext).get("propagateFocusChange");
    const pauseVideo = useContext(PlayerContext).get("pauseVideo");
    const setActiveFeature = useContext(PlayerContext).get("setActiveFeature");
    const activeFeature = useContext(PlayerContext).get("activeFeature");
    const appAnalyticsManager = useContext(PlayerContext).get("appAnalyticsManager");
    const playMode = useContext(PlayerContext).get("playMode");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const setPlayAudio = useContext(PlayerContext).get("setPlayAudio");
    const audioAlreadyDisabled = useContext(PlayerContext).get("audioAlreadyDisabled");
    const playAudio = useContext(PlayerContext).get("playAudio");
    const playerOnDesktop = useContext(PlayerContext).get("playerOnDesktop");
    const fromMaker = useContext(PlayerContext).get("fromMaker");
    const fromDemo = useContext(PlayerContext).get("fromDemo");
    const highlight = useContext(PlayerContext).get("highlight");
    const idPrefix = fromDemo ? (fromDemo +"-") : ""; 

    const [scope, animate] = useAnimate();

    useEffect(() => {
        if (highlight != null){

            let elementId = null;
            
            if (highlight.includes("near")){
                elementId = "secondary-btn-near";
            }            

            const element = document.getElementById(elementId);
            if (elementId != null && element != null){                   
                const animation = async () => {
                    await animate(element, { background: "#FFFFFFFF"}, {duration: 0.6});
                    if (element != null)
                        animate(element, { background: "#FFFFFF00"}, {duration: 0.6});
                }
                
                animation();
            }
        }
      }, [highlight, animate]);

    const panelRootStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: storyDimensions.containerWidth,
        height: storyDimensions.containerHeight,
        pointerEvents: 'auto',
        zIndex: 20,
    };

    if (props.story.posId == null && !hasChainRestaurants(props.story)) return (null);
        
    const open = () => {          
        if (!fromMaker /*&& !fromDemo*/ && !playerOnDesktop && !audioAlreadyDisabled && !playAudio){                        
            const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
            if (ambiantPlayer && ambiantPlayer.paused){
                try {
                    ambiantPlayer.play();                
                } catch(error){}
            }
            setPlayAudio(true);            
        }
        pauseVideo();
        openFullscreen();
        setActiveFeature("near");
        setIsOpened(true);
      
        analyticsManager.logCheckChatTag(props.storyId, playMode, true);
                
        if (appAnalyticsManager)
            appAnalyticsManager.clickedAttractions();

        if (getStoredPixelConsent() === 'accepted') {
            if (window.ttq){
                window.ttq.track('ViewContent', {                
                    "contents": [
                        {              
                            "content_id": "0",           
                            "content_name": "near",
                        }
                    ],
                });
            }
            if (window.fbq){                    
                window.fbq('track', 'ViewContent', {"content_name": "near"});
            }
        }
    };

    const close = () => {        
        propagateFocusChange(true);
        setIsOpened(false);
        setActiveFeature(null);
        analyticsManager.logCheckChatTag(props.storyId, playMode, false);
    };  
    
    function showCloseButton(){
        return (
            <div style={{
                position: 'absolute',
                bottom: 16 * sizeMultiplier,
                left: 0,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 40
            }}>
                
                <IconButton
                    onClick={close}
                    style={{
                        background: "#fff",
                        borderRadius: "50%",                                    
                        padding: 4 * sizeMultiplier,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}                        
                >                                     
                    <CloseIcon style={{ color: "#808080", fontSize: 24 * sizeMultiplier, width: 28 * sizeMultiplier, height: 28 * sizeMultiplier,}} />                        
                </IconButton>
            </div>
        );                    
    }
    
    if (props.show){                  
        if (isOpened && activeFeature === "near"){             

            const language = browserLocales(true)[0] === "fr" ? "fr" : "en";

            const sanitizeHtml = (html) => {
                if (!html) return "";
                return html
                    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                    .replace(/\n/g, '<br>')
                    .replace(/(<br\s*\/?>\s*)+$/gi, '');
            };

            const nearbyAttractionsRaw = typeof props.story.nearbyAttractions === 'object'
                ? (props.story.nearbyAttractions?.[language] || props.story.nearbyAttractions?.fr || props.story.nearbyAttractions?.en || "")
                : (props.story.nearbyAttractions || "");
            const nearbyAttractionsText = sanitizeHtml(unescapeHtml(nearbyAttractionsRaw));
            const nearbyAttractionsImg = props.story.nearbyAttractionsImg;
            const bandHeight = 30 * sizeMultiplier;
            const avatarSize = 3 * bandHeight;
            const cardRadius = 8 * sizeMultiplier;

            return (
                <FadeInPanel
                    className="near-container"
                    animationKey="near"
                    style={panelRootStyle}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        id={idPrefix + "gallery-overlay"}
                        className="BlockingOverlay"
                        style={{
                            width: storyDimensions.containerWidth,
                            height: storyDimensions.containerHeight,
                            background: "#FFFFFFD0",
                            pointerEvents: 'auto',
                        }}
                        onClick={close}
                    />

                    <div style={{
                        zIndex: 10,
                        position: 'absolute',
                        top: '50%',
                        left: 16 * sizeMultiplier,
                        right: 16 * sizeMultiplier,
                        transform: 'translateY(-50%)',
                        backgroundColor: '#FFFFFF',
                        borderRadius: cardRadius,
                        paddingBottom: 16 * sizeMultiplier,
                        maxHeight: storyDimensions.containerHeight * 0.7,
                        overflow: 'visible',
                        display: 'flex',
                        flexDirection: 'column',
                    }} onClick={(e)=>{e.stopPropagation();}}>
                        <div style={{
                            position: 'relative',
                            height: bandHeight,
                            backgroundColor: props.story.primaryColor,
                            borderTopLeftRadius: cardRadius,
                            borderTopRightRadius: cardRadius,
                            flexShrink: 0,
                        }}>
                            {nearbyAttractionsImg && (
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: 0,
                                    transform: 'translate(-50%, -50%)',
                                    width: avatarSize,
                                    height: avatarSize,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    zIndex: 2,
                                }}>
                                    <img
                                        src={nearbyAttractionsImg}
                                        alt={t('NearbyAttractionsAlt')}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{
                            overflowY: 'auto',
                            flex: 1,
                            minHeight: 0,
                        }}>
                            <div style={{
                                paddingLeft: 16 * sizeMultiplier,
                                paddingRight: 16 * sizeMultiplier,
                                paddingTop: nearbyAttractionsImg
                                    ? avatarSize / 2 - bandHeight + 16 * sizeMultiplier
                                    : 16 * sizeMultiplier,
                            }}>
                                <div style={{
                                    marginBottom: 16 * sizeMultiplier,
                                    fontSize: 18 * sizeMultiplier,
                                    fontWeight: 'bold',
                                    color: '#000000',
                                    textAlign: 'center',
                                    fontFamily: 'Roboto, sans-serif',
                                    lineHeight: 1.3,
                                }}>
                                    {t("NearbyAttractions")}
                                </div>

                                {nearbyAttractionsText && (
                                    <div
                                        className="card-html-content"
                                        style={{
                                            paddingTop: 8 * sizeMultiplier,
                                            paddingBottom: 8 * sizeMultiplier,
                                            fontSize: 14 * sizeMultiplier,
                                            fontWeight: 'normal',
                                            color: '#666666',
                                            lineHeight: 1.4,
                                            fontFamily: 'Roboto, sans-serif',
                                            textAlign: 'left',
                                        }}
                                        dangerouslySetInnerHTML={{__html: nearbyAttractionsText}}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {showCloseButton()}
                </FadeInPanel>
            );
        }
        else {                
            if (props.story.nearbyAttractions != null){                           
                return (
                    <SecondaryButton buttonType={nearType} open={open} story={props.story}/>
                );
            }
            else {                
                return null;
            }
        }                
    }
    else {        
        return null;
    }
}    

export default NearButton;